import { io } from 'socket.io-client'
import { tokenManager } from '@/services/apiClient'

let socket = null
let reconnectAttempts = 0
const MAX_RECONNECT_ATTEMPTS = 14
const RECONNECT_DELAY = 2000 // 2 second

export async function initSocket(idToken) {
    // Disconnect any existing socket before creating a new one
    if (socket) {
        console.log('Disconnecting existing socket before re-initialization...')
        socket.disconnect()
        socket = null
    }

    console.log('Initializing new socket connection...')

    // Use the provided idToken for the initial connection
    // No need to call tokenManager.getToken() here again for the initial setup
    if (!idToken) {
        console.error('No token provided for socket initialization')
        throw new Error(
            'Authentication token not available for initial connection'
        )
    }

    socket = io(import.meta.env.VITE_SOCKET_URL, {
        auth: { token: idToken }, // Use the passed idToken
        reconnection: true,
        reconnectionAttempts: MAX_RECONNECT_ATTEMPTS,
        reconnectionDelay: RECONNECT_DELAY,
        timeout: 20000,
        connectionStateRecovery: {},
    })

    // Connection event handlers
    socket.on('connect', () => {
        console.log('Socket connected:', socket.id)
        reconnectAttempts = 0 // Reset reconnect attempts on successful connection
    })

    socket.on('disconnect', (reason) => {
        console.log('Socket disconnected:', reason)
        if (reason === 'io server disconnect') {
            // Server initiated disconnect, try to reconnect
            socket.connect()
        }
    })

    socket.on('connect_error', async (error) => {
        console.error('Socket connection error:', error)

        // Check if error is due to token expiration (check both message and data.code)
        const isTokenExpired =
            error.message?.includes('id-token-expired') ||
            error.message?.includes('auth/id-token-expired') ||
            error.data?.code === 'auth/id-token-expired'

        if (isTokenExpired) {
            console.log('Token expired, attempting to refresh...')
            try {
                // Use forceRefresh to bypass cache and get a fresh token
                const newToken = await tokenManager.forceRefresh()
                if (newToken) {
                    console.log(
                        'Token refreshed successfully, reconnecting socket...'
                    )
                    // Update socket auth with new token
                    socket.auth = { token: newToken }
                    socket.connect()
                }
            } catch (refreshError) {
                console.error('Failed to refresh token:', refreshError)
            }
        }
    })

    // Handle reconnection attempts
    socket.io.on('reconnect_attempt', async (attempt) => {
        console.log(`Reconnection attempt ${attempt}/${MAX_RECONNECT_ATTEMPTS}`)
        reconnectAttempts = attempt

        if (attempt > 1) {
            // Try to refresh token before reconnecting
            try {
                // Use forceRefresh to ensure we get a fresh token
                const newToken = await tokenManager.forceRefresh()
                if (newToken) {
                    socket.auth = { token: newToken }
                }
            } catch (error) {
                console.error(
                    'Failed to refresh token during reconnection:',
                    error
                )
            }
        }
    })

    // Debug listener for rooms
    socket.on('your rooms', (rooms) => {
        console.log('Rooms I am in (debug):', rooms)
    })

    return socket
}

export function getSocket() {
    return socket
}

export function disconnectSocket() {
    if (socket) {
        socket.disconnect()
        socket = null
    }
}

export function getRooms() {
    const socket = getSocket()
    if (socket) {
        socket.emit('list my rooms')
    } else {
        console.warn('Socket not initialized')
    }
}

// Make it available globally for console access
window.getRooms = getRooms
